import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuActivity } from 'react-icons/lu';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { Avatar } from '../../ui/Labels';
import { Button } from '../../ui/Button';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useToast } from '../../ui/Toast';
import { useAuthStore } from '../../stores/auth';
import { trackerApi } from '../../lib/trackerApi';
import { CodingProfilesForm } from './CodingProfilesForm';
import { cx } from '../../ui/cx';

function LinkProfileModal({ onClose }) {
  const t = useStrings();
  const toast = useToast();
  const refreshTimer = useRef(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    trackerApi.mine().then((response) => {
      if (!cancelled && response.success) setProfile(response.data);
    });
    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer.current);
    };
  }, []);

  return (
    <div className="palette-backdrop profile-prompt" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="profile-prompt__dialog panel" role="dialog" aria-modal="true" aria-labelledby="link-profile-title">
        <h2 id="link-profile-title" className="panel__title">
          {t('profiles.promptTitle')}
        </h2>
        <p className="profile-prompt__text">{t('profiles.intro')}</p>
        {profile && (
          <CodingProfilesForm
            profile={profile}
            onSaved={(next) => {
              toast.show(t('profiles.saved'));
              const startedAt = next.fetchedAt;
              let attempts = 0;
              window.clearInterval(refreshTimer.current);
              refreshTimer.current = window.setInterval(async () => {
                attempts += 1;
                const response = await trackerApi.mine();
                if (response.success) {
                  window.dispatchEvent(new Event('tracker-refresh'));
                  if (response.data.fetchedAt && response.data.fetchedAt !== startedAt) {
                    window.clearInterval(refreshTimer.current);
                  }
                }
                if (attempts >= 36) window.clearInterval(refreshTimer.current);
              }, 5000);
              onClose();
            }}
            onSkip={onClose}
          />
        )}
      </div>
    </div>
  );
}

function Count({ value }) {
  if (value === null) return <span className="subtle">—</span>;
  return <span className="tabular">{value}</span>;
}

export function TrackerPage() {
  const t = useStrings();
  const user = useAuthStore((state) => state.user);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('loading');
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  useDocumentTitle(t('tracker.title'));

  const load = useCallback(async () => {
    setStatus('loading');
    const response = await trackerApi.list();
    if (!response.success) {
      setError(response.error);
      setStatus('error');
      return;
    }
    setRows(response.data.rows);
    setError(null);
    setStatus('ready');
  }, []);

  useEffect(() => {
    const onRefresh = () => {
      trackerApi.list().then((response) => {
        if (response.success) setRows(response.data.rows);
      });
    };
    window.addEventListener('tracker-refresh', onRefresh);
    return () => window.removeEventListener('tracker-refresh', onRefresh);
  }, []);

  useEffect(() => {
    let cancelled = false;
    trackerApi.list().then((response) => {
      if (cancelled) return;
      if (!response.success) {
        setError(response.error);
        setStatus('error');
        return;
      }
      setRows(response.data.rows);
      setStatus('ready');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('tracker.title')}</h1>
          <p className="page-subtitle">{t('tracker.intro')}</p>
        </div>
        <Button size="sm" onClick={() => setLinking(true)}>
          {t('profiles.linkProfile')}
        </Button>
      </header>
      {linking && <LinkProfileModal onClose={() => setLinking(false)} />}

      {status === 'error' && (
        <Alert tone="danger" action={<Button size="sm" onClick={load}>{t('common.retry')}</Button>}>
          {t('tracker.loadError')} {error}
        </Alert>
      )}

      {status === 'loading' && (
        <div className="panel">
          <div className="panel__body history-skeleton">
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </div>
        </div>
      )}

      {status === 'ready' && rows.length === 0 && (
        <div className="panel">
          <EmptyState icon={LuActivity} title={t('tracker.emptyTitle')} text={t('tracker.emptyText')} />
        </div>
      )}

      {status === 'ready' && rows.length > 0 && (
        <div className="table-card">
          <div className="standings-scroll">
            <table className="data-table tracker-table">
              <thead>
                <tr>
                  <th className="is-numeric">{t('tracker.columnRank')}</th>
                  <th>{t('tracker.columnName')}</th>
                  <th className="is-numeric">{t('tracker.columnAlgoForge')}</th>
                  <th className="is-numeric">{t('tracker.columnLeetCode')}</th>
                  <th className="is-numeric">{t('tracker.columnCodeforces')}</th>
                  <th className="is-numeric">{t('tracker.columnCodeChef')}</th>
                  <th className="is-numeric">{t('tracker.columnTotal')}</th>
                </tr>
              </thead>
              <tbody className="stagger">
                {rows.map((row, index) => (
                  <tr key={row.userId} className={cx(row.userId === user?.id && 'is-me')} style={{ '--i': Math.min(index, 12) }}>
                    <td className="is-numeric tabular">{row.rank}</td>
                    <td>
                      <Link to={`/profile/${row.userId}`} className="standings-table__person tracker-person" viewTransition>
                        <Avatar name={row.username} size="sm" />
                        <span className="standings-table__name">{row.username}</span>
                      </Link>
                    </td>
                    <td className="is-numeric"><Count value={row.algoforgeSolved} /></td>
                    <td className="is-numeric"><Count value={row.leetcodeSolved} /></td>
                    <td className="is-numeric"><Count value={row.codeforcesSolved} /></td>
                    <td className="is-numeric"><Count value={row.codechefSolved} /></td>
                    <td className="is-numeric tabular tracker-table__total">{row.totalSolved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
